import React, { useEffect, useState } from 'react'
import { Image, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import { useTranslation } from 'react-i18next'
import LoadingSpinner from 'components/LoadingSpinner'
import {
  isMimeTypeExcel, isMimeTypeImage, isMimeTypePdf, isMimeTypeWord,
} from 'helpers'
import { DocumentFile } from 'types/Document'
import { FileType } from 'features/declaration/models/schemas/fileSchemas'
import { hasText } from 'shared/lib/common-util'
import { toast } from 'react-toastify'
import fetchSignedFileUrl from 'shared/file/api/fetchSignedFileUrl'
import { FileFolder } from 'shared/file/models/types'
import reportApiFailure from 'shared/api/reportApiFailure'
import DocumentService from '../../../../routes/Declaration/services/document.service'
import { FilePreviewModal } from '../../../../routes/Transits/TransitsAside/FilePreviewModal'

export interface TransitPreviewDocumentFilesSectionProps {
  files: FileType[]
}

function TransitPreviewDocumentFilesSection({ files }: TransitPreviewDocumentFilesSectionProps) {
  const { t } = useTranslation()
  const [filesWithUrls, setFilesWithUrls] = useState<Array<FileType & { signedUrl: string }> | null>(null)
  const [isPreviewModalVisible, setPreviewModalVisibility] = useState(false)
  const [previewModalFile, setPreviewModalFile] = useState<DocumentFile>()

  useEffect(() => {
    Promise.all(files.map(async (file: FileType) => {
      const fileUuid = file.uuid ?? ''
      const fileParts = fileUuid.split('/')
      const signedUrl = await fetchSignedFileUrl({
        folder: fileParts[0] as FileFolder,
        fileUuid: fileParts[1],
      })

      return {
        ...file,
        signedUrl,
      }
    })).then((result) => setFilesWithUrls(result))
      .catch((error) => {
        setFilesWithUrls([])
        reportApiFailure(error, () => toast.error(t('common.error')))
      })
  }, [files])

  const getFilePreviewTypeByMimeType = (mimeType: string) => {
    if (isMimeTypeImage(mimeType)) {
      return 'IMAGE'
    }
    if (isMimeTypeExcel(mimeType)) {
      return 'EXCEL'
    }
    if (isMimeTypeWord(mimeType)) {
      return 'WORD'
    }

    return isMimeTypePdf(mimeType) ? 'PDF' : null
  }

  const downloadOrOpenFile = (file: DocumentFile) => {
    if (file.url) {
      const filePreviewType = getFilePreviewTypeByMimeType(file.mimeType)
      switch (filePreviewType) {
        case 'IMAGE':
        case 'PDF':
        case 'EXCEL':
        case 'WORD':
          setPreviewModalFile(file)
          setPreviewModalVisibility(!isPreviewModalVisible)
          break
        default:
          DocumentService.saveBlob(file, file.url, 'url')
      }
    }
  }

  const renderFilePreviewModal = () => {
    if (!previewModalFile) {
      return null
    }

    const filePreviewType = getFilePreviewTypeByMimeType(previewModalFile.mimeType)
    if (!filePreviewType) {
      return null
    }

    return (
      <FilePreviewModal
        type={filePreviewType}
        key="filePreviewModal"
        isVisible={isPreviewModalVisible}
        toggleVisibility={() => setPreviewModalVisibility(!isPreviewModalVisible)}
        file={previewModalFile}
      />
    )
  }

  const renderFileButton = ({
    id: fileId,
    fileName,
    mimeType,
    uuid,
    sequenceNumber,
    signedUrl,
  }: FileType & { signedUrl: string }) => {
    const filename = fileName ?? ''
    const filePreviewType = getFilePreviewTypeByMimeType(mimeType)
    if (filePreviewType === 'IMAGE') {
      return (
        <div className="overflow-x-auto">
          <OverlayTrigger
            trigger={['hover', 'focus']}
            delay={500}
            overlay={(
              <Tooltip id="tooltip-thumbnail">
                <Image
                  alt={filename}
                  src={signedUrl}
                  style={{
                    maxWidth: '10rem',
                  }}
                />
              </Tooltip>
            )}
          >
            <Button
              className="pb-1 p-0 overflow-ellipsis text-left"
              variant="link"
              onClick={() => downloadOrOpenFile({
                id: fileId,
                file: null,
                uuid,
                filename,
                mimeType,
                sortOrder: sequenceNumber,
                url: signedUrl,
              })}
            >
              <i
                className="fal fa-image fa-md me-1"
              />
              <span>{fileName}</span>
            </Button>
          </OverlayTrigger>
        </div>
      )
    }

    if (filePreviewType === 'PDF') {
      return (
        <div className="overflow-x-auto">
          <Button
            className="pb-1 p-0 overflow-ellipsis text-left"
            variant="link"
            onClick={() => {
              if (hasText(signedUrl)) {
                downloadOrOpenFile({
                  id: fileId,
                  file: null,
                  uuid,
                  filename,
                  mimeType,
                  sortOrder: sequenceNumber,
                  url: signedUrl,
                })
              }
            }}
          >
            <i
              className="fal fa-expand-arrows-alt fa-file-pdf fa-md me-1"
            />
            <span>{fileName}</span>
          </Button>
        </div>
      )
    }

    if (filePreviewType === 'EXCEL') {
      return (
        <div className="overflow-x-auto">
          <Button
            className="pb-1 p-0 overflow-ellipsis text-left"
            variant="link"
            onClick={() => {
              downloadOrOpenFile({
                id: fileId,
                file: null,
                uuid,
                filename,
                mimeType,
                sortOrder: sequenceNumber,
                url: signedUrl,
              })
            }}
          >
            <i
              className="fal fa-expand-arrows-alt fa-file-spreadsheet fa-md me-1"
            />
            <span>{fileName}</span>
          </Button>
        </div>
      )
    }

    if (filePreviewType === 'WORD') {
      return (
        <div className="overflow-x-auto">
          <Button
            className="pb-1 p-0 overflow-ellipsis text-left"
            variant="link"
            onClick={() => {
              downloadOrOpenFile({
                id: fileId,
                file: null,
                uuid,
                filename,
                mimeType,
                sortOrder: sequenceNumber,
                url: signedUrl,
              })
            }}
          >
            <i
              className="fal fa-expand-arrows-alt fa-file-word fa-md me-1"
            />
            <span>{fileName}</span>
          </Button>
        </div>
      )
    }

    return (
      <div className="overflow-x-auto">
        <Button
          className="pb-1 p-0 overflow-ellipsis text-left"
          variant="link"
          onClick={() => {
            downloadOrOpenFile({
              id: fileId,
              file: null,
              uuid,
              filename,
              mimeType,
              sortOrder: sequenceNumber,
              url: signedUrl,
            })
          }}
        >
          <i
            className="fal fa-expand-arrows-alt fa-file fa-md me-1"
          />
          <span>{fileName}</span>
        </Button>
      </div>
    )
  }

  return (
    <div className="col-12">
      <div className="row mb-2">
        <div className="col-5 d-flex text-muted input-label pt-0">
          {t('declaration.p5.files')}
        </div>
        {
          filesWithUrls == null && (
            <div className="col-7 ">
              <LoadingSpinner />
            </div>
          )
        }
        {
          filesWithUrls != null && (
            <div className="col-7 ">
              {
                filesWithUrls.map((fileWithUrl) => (
                  <div className="d-flex" key={`${fileWithUrl.uuid}`}>
                    {renderFileButton(fileWithUrl)}
                  </div>
                ))
              }
            </div>
          )
        }
      </div>
      {
        previewModalFile && isPreviewModalVisible && renderFilePreviewModal()
      }
    </div>
  )
}

export default TransitPreviewDocumentFilesSection
